/**
 * Tenant Admin Console — Sprint 41: Customer Management
 *
 * Sections:
 *  Dashboard   — Enhanced KPIs + charts + top5
 *  Kunden      — Sortable/filterable table + CustomerDetail slide-in panel
 *  Abonnements — Subscription overview (uses customer data)
 *  Ankündigungen — Send system announcement
 *  Einstellungen — Branding + API key
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import IntegrationsSection from './integrations/IntegrationsSection';

const ALLOWED_ROLES = new Set(['tenant_admin', 'alcedo_operator']);

export default function ConsolePage({ onClose, token, user }) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  if (!user || !ALLOWED_ROLES.has(user.role)) {
    return (
      <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex items-center justify-center">
        <div className="text-center space-y-3 px-6">
          <div className="text-3xl">🔒</div>
          <p className="text-sm text-[var(--aif-text-muted)]">{t('console.accessDenied')}</p>
          <button onClick={onClose} className="text-xs text-[var(--aif-color-primary)] underline">
            {t('console.backToApp')}
          </button>
        </div>
      </div>
    );
  }

  const SECTIONS = [
    { key: 'dashboard', label: t('console.dashboard'), icon: '📊' },
    { key: 'customers', label: t('console.customers'), icon: '👥' },
    { key: 'revenue', label: t('console.revenueTab'), icon: '💶' },
    { key: 'subscriptions', label: t('console.subsTab'), icon: '💳' },
    { key: 'coupons', label: t('console.couponsTab'), icon: '🎟️' },
    { key: 'support', label: t('console.supportTab'), icon: '🎫' },
    { key: 'content', label: t('console.contentTab'), icon: '📝' },
    { key: 'subsidy_data', label: t('console.subsidyTab'), icon: '🌾' },
    { key: 'reference_data', label: t('console.referenceTab'), icon: '📋' },
    { key: 'announcements', label: t('console.announcementsSection'), icon: '📢' },
    { key: 'integrations', label: 'Integrations', icon: '🔗' },
    { key: 'settings', label: t('console.settings'), icon: '⚙️' },
  ];

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0 bg-[var(--aif-bg-surface)]">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-elevated)] transition text-[var(--aif-text-muted)]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div>
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('console.title')}</span>
          {user.role === 'alcedo_operator' && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">PLATFORM</span>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <nav className="w-52 border-r border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] flex-shrink-0 pt-2 hidden sm:block">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setActiveSection(s.key); setSelectedCustomer(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition ${
                activeSection === s.key
                  ? 'bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] font-medium'
                  : 'text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
              }`}
            >
              <span>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>

        {/* Mobile tab bar */}
        <div className="flex sm:hidden border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] overflow-x-auto absolute bottom-0 left-0 right-0 z-10">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setActiveSection(s.key); setSelectedCustomer(null); }}
              className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 text-[9px] transition ${
                activeSection === s.key
                  ? 'text-[var(--aif-color-primary)] border-t-2 border-[var(--aif-color-primary)]'
                  : 'text-[var(--aif-text-muted)]'
              }`}
            >
              <span className="text-base">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Main content + optional CustomerDetail panel */}
        <div className="flex flex-1 overflow-hidden">
          <main className={`overflow-y-auto p-4 sm:p-6 transition-all ${selectedCustomer ? 'w-1/2 hidden sm:block' : 'flex-1'}`}>
            {activeSection === 'dashboard' && <ConsoleDashboard token={token} t={t} onSelectCustomer={id => { setSelectedCustomer(id); setActiveSection('customers'); }} />}
            {activeSection === 'customers' && (
              <ConsoleCustomers
                token={token}
                t={t}
                selectedId={selectedCustomer}
                onSelect={setSelectedCustomer}
              />
            )}
            {activeSection === 'revenue' && <ConsoleRevenue token={token} t={t} />}
            {activeSection === 'subscriptions' && <ConsoleSubscriptions token={token} t={t} onSelect={id => { setSelectedCustomer(id); setActiveSection('customers'); }} />}
            {activeSection === 'coupons' && <ConsoleCoupons token={token} t={t} />}
            {activeSection === 'support' && <ConsoleSupport token={token} t={t} />}
            {activeSection === 'content' && <ConsoleContent token={token} t={t} />}
            {activeSection === 'subsidy_data' && <ConsoleSubsidyData token={token} t={t} />}
            {activeSection === 'reference_data' && <ConsoleReferenceData token={token} t={t} />}
            {activeSection === 'announcements' && <ConsoleAnnouncements token={token} t={t} />}
            {activeSection === 'integrations' && <IntegrationsSection tenantId={user?.tenant_id} />}
            {activeSection === 'settings' && <ConsoleSettings token={token} t={t} user={user} />}
          </main>

          {/* CustomerDetail slide-in panel */}
          {selectedCustomer && (
            <div className="w-full sm:w-1/2 border-l border-[var(--aif-border-default)] bg-[var(--aif-bg-page)] overflow-y-auto flex-shrink-0">
              <CustomerDetail
                token={token}
                t={t}
                customerId={selectedCustomer}
                onClose={() => setSelectedCustomer(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Dashboard ─────────────────────────────────────────────────

function ConsoleDashboard({ token, t, onSelectCustomer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/console/dashboard', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <ErrorCard message={t('console.loadError')} />;

  const kpis = data.kpis || {};
  const tierCounts = data.customers_by_tier || {};
  const top5 = data.top5_active || [];
  const dq = data.data_quality || {};

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{data.tenant_name}</h2>
        <p className="text-xs text-[var(--aif-text-muted)]">
          {t('console.tenantSlug')}: <span className="font-mono">{data.tenant_slug}</span>
          {' · '}{t('console.revenueSplit')}: {data.revenue_split_pct}%
        </p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard value={kpis.total_customers} label={t('console.kpiTotalCustomers')} icon="👨‍🌾" />
        <KpiCard value={kpis.active_30d} label={t('console.kpiActive30d')} icon="🟢" />
        <KpiCard value={kpis.active_7d} label={t('console.kpiActive7d')} icon="⚡" />
        <KpiCard value={kpis.new_this_month} label={t('console.kpiNewThisMonth')} icon="✨" />
        <KpiCard
          value={kpis.mrr_eur_estimate != null ? `${kpis.mrr_eur_estimate.toLocaleString('de-DE')} €` : '—'}
          label={t('console.kpiMrr')}
          icon="💶"
        />
        <KpiCard
          value={kpis.arr_eur_estimate != null ? `${kpis.arr_eur_estimate.toLocaleString('de-DE')} €` : '—'}
          label={t('console.kpiArr')}
          icon="📈"
        />
        <KpiCard
          value={kpis.cost_eur_this_month != null ? `${kpis.cost_eur_this_month.toFixed(2)} €` : '—'}
          label={t('console.kpiCostMonth')}
          icon="🤖"
        />
        <KpiCard value={kpis.messages_this_month} label={t('console.kpiMessagesMonth')} icon="💬" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Tier distribution */}
        {Object.keys(tierCounts).length > 0 && (
          <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
            <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
              {t('console.subscriptionBreakdown')}
            </h3>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(tierCounts).map(([tier, count]) => (
                <div key={tier} className="text-center">
                  <div className="text-xl font-bold text-[var(--aif-text-primary)]">{count}</div>
                  <div className={`text-[10px] font-medium mt-0.5 capitalize ${
                    tier === 'pro' ? 'text-purple-400' :
                    tier === 'premium' ? 'text-[var(--aif-color-primary)]' :
                    'text-[var(--aif-text-muted)]'
                  }`}>{tier}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data quality */}
        {dq.total_customers > 0 && (
          <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
            <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
              {t('console.kpiDataQuality')}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-[var(--aif-text-muted)]">{t('console.avgCompleteness')}</span>
                <span className="font-semibold text-[var(--aif-text-primary)]">{dq.avg_completeness_pct}%</span>
              </div>
              <div className="h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--aif-color-primary)] rounded-full transition-all"
                  style={{ width: `${dq.avg_completeness_pct}%` }}
                />
              </div>
              <p className="text-[10px] text-[var(--aif-text-muted)]">
                {t('console.goodDataCustomers', { good: dq.customers_with_good_data, total: dq.total_customers })}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Growth chart */}
      {data.growth_chart && data.growth_chart.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
            {t('console.growthChart')}
          </h3>
          <MiniBarChart data={data.growth_chart} valueKey="new_customers" labelKey="week" />
        </div>
      )}

      {/* Top 5 active */}
      {top5.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
            {t('console.top5Active')}
          </h3>
          <div className="space-y-2">
            {top5.map((c, i) => (
              <div
                key={c.user_id}
                onClick={() => onSelectCustomer(c.user_id)}
                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-[var(--aif-bg-elevated)] cursor-pointer transition"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--aif-text-muted)] w-4">{i + 1}.</span>
                  <div>
                    <span className="text-xs text-[var(--aif-text-primary)]">{c.name}</span>
                    <TierBadge tier={c.tier} />
                  </div>
                </div>
                <span className="text-xs font-semibold text-[var(--aif-text-primary)]">
                  {c.messages_this_month} {t('console.msgShort')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Customer List ─────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'last_active', labelKey: 'sortLastActive' },
  { value: 'created_at', labelKey: 'sortCreated' },
  { value: 'message_count', labelKey: 'sortMessages' },
  { value: 'total_ha', labelKey: 'sortHa' },
];

const TIER_OPTIONS = ['', 'free', 'premium', 'pro'];

function ConsoleCustomers({ token, t, selectedId, onSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('last_active');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterTier, setFilterTier] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);

  const load = useCallback((p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, per_page: 20, sort_by: sortBy, sort_order: sortOrder });
    if (search) params.set('search', search);
    if (filterTier) params.set('tier', filterTier);
    if (filterActiveOnly) params.set('active_only', 'true');

    apiClient.get(`/api/v1/aif/console/customers?${params}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, search, sortBy, sortOrder, filterTier, filterActiveOnly]);

  useEffect(() => { if (token) load(page); }, [token, page, sortBy, sortOrder, filterTier, filterActiveOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(1);
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortHeader = ({ col, label }) => (
    <th
      onClick={() => toggleSort(col)}
      className="text-left px-3 py-2 text-[var(--aif-text-muted)] font-medium cursor-pointer hover:text-[var(--aif-text-primary)] select-none"
    >
      {label}
      {sortBy === col && <span className="ml-1 text-[var(--aif-color-primary)]">{sortOrder === 'desc' ? '↓' : '↑'}</span>}
    </th>
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.customers')}</h2>

      {/* Search + filters */}
      <div className="space-y-2">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('console.searchPlaceholder')}
            className="flex-1 px-3 py-2 rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none focus:border-[var(--aif-color-primary)]/50"
          />
          <button type="submit" className="px-4 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm">
            {t('console.search')}
          </button>
        </form>

        <div className="flex gap-2 flex-wrap">
          <select
            value={filterTier}
            onChange={e => { setFilterTier(e.target.value); setPage(1); }}
            className="px-2 py-1.5 rounded-lg bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none"
          >
            <option value="">{t('console.filterAll')}</option>
            {['free', 'premium', 'pro'].map(tier => (
              <option key={tier} value={tier}>{tier}</option>
            ))}
          </select>

          <label className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={filterActiveOnly}
              onChange={e => { setFilterActiveOnly(e.target.checked); setPage(1); }}
              className="accent-[var(--aif-color-primary)]"
            />
            {t('console.filterActive')}
          </label>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : !data ? <ErrorCard message={t('console.loadError')} /> : (
        <>
          <p className="text-xs text-[var(--aif-text-muted)]">{data.total} {t('console.customersTotal')}</p>

          <div className="rounded-xl border border-[var(--aif-border-default)] overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-[var(--aif-bg-surface)]">
                <tr>
                  <SortHeader col="last_active" label={t('console.colName')} />
                  <th className="text-left px-3 py-2 text-[var(--aif-text-muted)] font-medium hidden sm:table-cell">{t('console.colFarm')}</th>
                  <SortHeader col="total_ha" label={t('console.colHa')} />
                  <th className="text-left px-3 py-2 text-[var(--aif-text-muted)] font-medium">{t('console.colTier')}</th>
                  <SortHeader col="message_count" label={t('console.colMessages')} />
                  <SortHeader col="created_at" label={t('console.colLastLogin')} />
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c, i) => (
                  <tr
                    key={c.id}
                    onClick={() => onSelect(c.id === selectedId ? null : c.id)}
                    className={`border-t border-[var(--aif-border-default)] cursor-pointer transition ${
                      c.id === selectedId
                        ? 'bg-[var(--aif-color-primary)]/5'
                        : i % 2 === 0 ? 'bg-[var(--aif-bg-page)] hover:bg-[var(--aif-bg-elevated)]'
                          : 'bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-[var(--aif-text-primary)]">{c.name}</div>
                      <div className="text-[var(--aif-text-muted)]">{c.email}</div>
                      {!c.active && <span className="text-red-400 text-[10px]">{t('console.inactive')}</span>}
                    </td>
                    <td className="px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">
                      {c.farm_name || '—'}
                      {c.country_code && <span className="ml-1 text-[10px]">({c.country_code})</span>}
                    </td>
                    <td className="px-3 py-2 text-[var(--aif-text-secondary)]">
                      {c.total_ha != null ? `${c.total_ha} ha` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <TierBadge tier={c.subscription_tier} />
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--aif-text-primary)]">{c.message_count}</td>
                    <td className="px-3 py-2 text-[var(--aif-text-muted)]">
                      {c.last_active_at ? new Date(c.last_active_at).toLocaleDateString('de-DE') : '—'}
                    </td>
                  </tr>
                ))}
                {data.customers.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--aif-text-muted)]">{t('console.noCustomers')}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {data.total > 20 && (
            <div className="flex justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 rounded-lg text-xs border border-[var(--aif-border-default)] disabled:opacity-40"
              >←</button>
              <span className="px-3 py-1 text-xs text-[var(--aif-text-muted)]">
                {page} / {Math.ceil(data.total / 20)}
              </span>
              <button
                disabled={page >= Math.ceil(data.total / 20)}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 rounded-lg text-xs border border-[var(--aif-border-default)] disabled:opacity-40"
              >→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── Customer Detail Panel ─────────────────────────────────────

function CustomerDetail({ token, t, customerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [newTier, setNewTier] = useState('');
  const [tierSaving, setTierSaving] = useState(false);
  const [tierSaved, setTierSaved] = useState(false);
  const [impToken, setImpToken] = useState(null);
  const [impCopied, setImpCopied] = useState(false);
  const [subData, setSubData] = useState(null);

  useEffect(() => {
    if (!customerId || !token) return;
    setLoading(true);
    setData(null);
    setImpToken(null);

    Promise.all([
      apiClient.get(`/api/v1/aif/console/customers/${customerId}`).then(r => r.data).catch(() => null),
      apiClient.get(`/api/v1/aif/console/customers/${customerId}/subscription`).then(r => r.data).catch(() => null),
    ]).then(([detail, sub]) => {
      setData(detail);
      setSubData(sub);
      if (detail) {
        setNotes(detail.admin_notes || '');
        setNewTier((detail.subscription_tier) || 'free');
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [customerId, token]);

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/console/customers/${customerId}/notes`, { notes }, {
        entityType: 'customer', queueOnOffline: true,
      });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setNotesSaving(false);
  };

  const saveTier = async () => {
    setTierSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/console/customers/${customerId}/subscription`, { tier: newTier }, {
        entityType: 'customer', queueOnOffline: true,
      });
      setTierSaved(true);
      setTimeout(() => setTierSaved(false), 3000);
      setData(d => d ? { ...d, subscription_tier: newTier } : d);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setTierSaving(false);
  };

  const startImpersonation = async () => {
    try {
      const r = await apiClient.post(`/api/v1/aif/console/customers/${customerId}/impersonate`, {}, {
        entityType: 'customer', queueOnOffline: true,
      });
      if (r?.data?.token) setImpToken(r.data.token);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const copyToken = () => {
    if (impToken) {
      navigator.clipboard.writeText(impToken);
      setImpCopied(true);
      setTimeout(() => setImpCopied(false), 2000);
    }
  };

  if (loading) return (
    <div className="p-4 space-y-3">
      <button onClick={onClose} className="text-xs text-[var(--aif-text-muted)]">← {t('menu.close')}</button>
      <LoadingSpinner />
    </div>
  );
  if (!data) return (
    <div className="p-4">
      <button onClick={onClose} className="text-xs text-[var(--aif-text-muted)] mb-3">← {t('menu.close')}</button>
      <ErrorCard message={t('console.loadError')} />
    </div>
  );

  const farm = data.farm;
  const usage = data.usage || {};
  const TABS = [
    { key: 'overview', label: t('console.tabOverview') },
    { key: 'usage', label: t('console.tabUsage') },
    { key: 'farmdata', label: t('console.tabFarmData') },
  ];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">{data.name}</h3>
          <p className="text-xs text-[var(--aif-text-muted)]">{data.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <TierBadge tier={data.subscription_tier} />
            {data.email_verified && <span className="text-[10px] text-green-400">✓ {t('console.verified')}</span>}
          </div>
        </div>
        <button onClick={onClose} className="text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] p-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[var(--aif-border-default)] gap-4">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-2 text-xs transition ${
              activeTab === tab.key
                ? 'text-[var(--aif-color-primary)] border-b-2 border-[var(--aif-color-primary)] font-medium'
                : 'text-[var(--aif-text-muted)] hover:text-[var(--aif-text-secondary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Übersicht */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-2">
            <MiniKpi label={t('console.metricHa')} value={farm?.total_ha != null ? `${farm.total_ha} ha` : '—'} />
            <MiniKpi label={t('console.metricCompleteness')} value={farm ? `${farm.profile_completeness}%` : '—'} />
            <MiniKpi label={t('console.metricFields')} value={farm?.fields_count ?? '—'} />
            <MiniKpi label={t('console.metricCrops')} value={farm?.crops_count ?? '—'} />
          </div>

          {/* Subscription management */}
          <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2">
            <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide">
              {t('console.subscriptionCurrent')}
            </h4>
            <div className="flex items-center gap-2">
              <select
                value={newTier}
                onChange={e => setNewTier(e.target.value)}
                className="flex-1 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none"
              >
                {['free', 'premium', 'pro', 'enterprise'].map(t2 => (
                  <option key={t2} value={t2}>{t2}</option>
                ))}
              </select>
              <button
                onClick={saveTier}
                disabled={tierSaving}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  tierSaved
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-[var(--aif-color-primary)] text-white hover:opacity-90'
                }`}
              >
                {tierSaving ? '…' : tierSaved ? `✓` : t('console.subscriptionChangeSave')}
              </button>
            </div>
            <p className="text-[10px] text-amber-400">{t('console.subscriptionWarning')}</p>
          </div>

          {/* Admin notes */}
          <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2">
            <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide">
              {t('console.notesLabel')}
            </h4>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('console.notesPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none resize-none"
            />
            <button
              onClick={saveNotes}
              disabled={notesSaving}
              className={`w-full py-1.5 rounded-lg text-xs font-medium transition ${
                notesSaved
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] hover:text-[var(--aif-text-primary)] border border-[var(--aif-border-default)]'
              }`}
            >
              {notesSaving ? '…' : notesSaved ? `✓ ${t('console.notesSaved')}` : t('console.notesSave')}
            </button>
          </div>

          {/* Impersonation */}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
            <h4 className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide">
              {t('console.impersonateTitle')}
            </h4>
            <p className="text-[10px] text-[var(--aif-text-muted)]">{t('console.impersonateWarning')}</p>
            {!impToken ? (
              <button
                onClick={startImpersonation}
                className="w-full py-1.5 rounded-lg text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition"
              >
                {t('console.impersonateBtn')}
              </button>
            ) : (
              <div className="space-y-1.5">
                <p className="text-[10px] text-[var(--aif-text-muted)]">{t('console.impersonateToken')}:</p>
                <div className="flex gap-1.5">
                  <code className="flex-1 text-[9px] font-mono bg-[var(--aif-bg-elevated)] px-2 py-1 rounded overflow-hidden text-ellipsis whitespace-nowrap text-[var(--aif-text-muted)]">
                    {impToken.slice(0, 40)}…
                  </code>
                  <button
                    onClick={copyToken}
                    className="px-2 py-1 rounded text-[10px] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] hover:text-[var(--aif-text-primary)]"
                  >
                    {impCopied ? t('console.impersonateCopied') : t('console.impersonateCopy')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Advisor accesses */}
          {data.advisor_accesses && (
            <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
              <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                {t('console.advisorAccesses')}
              </h4>
              {data.advisor_accesses.length === 0 ? (
                <p className="text-xs text-[var(--aif-text-muted)]">{t('console.noAdvisors')}</p>
              ) : (
                <div className="space-y-1">
                  {data.advisor_accesses.map((a, i) => (
                    <div key={i} className="text-xs text-[var(--aif-text-secondary)]">
                      {a.advisor_name || a.advisor_email || t('console.advisor')}
                      {a.expires_at && <span className="text-[10px] text-[var(--aif-text-muted)] ml-2">bis {new Date(a.expires_at).toLocaleDateString('de-DE')}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab: Nutzung */}
      {activeTab === 'usage' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <MiniKpi label={t('console.totalMessages')} value={usage.total_messages} />
            <MiniKpi label={t('console.metricMessagesMonth')} value={usage.messages_this_month} />
            <MiniKpi label={t('console.metricCostMonth')} value={usage.cost_eur_this_month != null ? `${usage.cost_eur_this_month.toFixed(4)} €` : '—'} />
            <MiniKpi label={t('console.subsidyApplications')} value={data.subsidy_tracking_count ?? 0} />
          </div>

          {/* Top intents */}
          {usage.top_intents && usage.top_intents.length > 0 ? (
            <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
              <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                {t('console.metricTopIntents')}
              </h4>
              <div className="space-y-1.5">
                {usage.top_intents.map(({ intent, count }) => (
                  <div key={intent} className="flex justify-between items-center">
                    <span className="text-xs text-[var(--aif-text-secondary)] capitalize">{intent?.replace(/_/g, ' ') || t('console.otherIntent')}</span>
                    <span className="text-[10px] text-[var(--aif-text-muted)]">{count}×</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--aif-text-muted)] text-center py-4">{t('console.noIntents')}</p>
          )}

          {/* Subscription history */}
          {subData?.change_history && subData.change_history.length > 0 && (
            <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
              <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                {t('console.tierHistory')}
              </h4>
              <div className="space-y-1">
                {subData.change_history.map((h, i) => (
                  <div key={i} className="text-xs text-[var(--aif-text-secondary)] flex justify-between">
                    <span>{h.tier_before} → {h.tier_after}</span>
                    <span className="text-[10px] text-[var(--aif-text-muted)]">
                      {h.timestamp ? new Date(h.timestamp).toLocaleDateString('de-DE') : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Betriebsdaten */}
      {activeTab === 'farmdata' && (
        <div className="space-y-3">
          {!farm ? (
            <p className="text-xs text-[var(--aif-text-muted)] text-center py-6">{t('console.noFarmData')}</p>
          ) : (
            <>
              <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2">
                <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide">{t('console.tabFarmData')}</h4>
                <FarmRow label="Name" value={farm.name} />
                <FarmRow label={t('console.colCountry')} value={farm.country_code} />
                <FarmRow label={t('console.farmRegion')} value={farm.region_code} />
                <FarmRow label={t('console.colHa')} value={farm.total_ha != null ? `${farm.total_ha} ha` : null} />
                <FarmRow label={t('console.farmLegalForm')} value={farm.legal_form} />
                <FarmRow label={t('console.farmPostalCode')} value={farm.postal_code} />
                {farm.organic_certified && (
                  <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                    ✓ {t('console.farmOrganic')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <MiniKpi label={t('console.metricFields')} value={farm.fields_count} />
                <MiniKpi label={t('console.metricCrops')} value={farm.crops_count} />
                <MiniKpi label={t('console.metricHerds')} value={farm.livestock_count} />
                <MiniKpi label={t('console.metricMachines')} value={farm.machines_count} />
              </div>

              {farm.latitude && farm.longitude && (
                <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
                  <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">
                    {t('console.farmGPS')}
                  </h4>
                  <p className="text-xs font-mono text-[var(--aif-text-secondary)]">
                    {farm.latitude.toFixed(4)}, {farm.longitude.toFixed(4)}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
                <h4 className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">
                  {t('console.metricCompleteness')}
                </h4>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--aif-color-primary)] rounded-full"
                      style={{ width: `${farm.profile_completeness}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-[var(--aif-text-primary)]">{farm.profile_completeness}%</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FarmRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between">
      <span className="text-[10px] text-[var(--aif-text-muted)]">{label}</span>
      <span className="text-xs text-[var(--aif-text-secondary)]">{value}</span>
    </div>
  );
}


// ── Announcements ─────────────────────────────────────────────

function ConsoleAnnouncements({ token, t }) {
  const [form, setForm] = useState({ title: '', message: '', severity: 'info' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    if (!form.title || !form.message) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const r = await apiClient.post('/api/v1/aif/console/announcements', form, {
        entityType: 'announcement', queueOnOffline: true,
      });
      setResult(r.data);
      setForm({ title: '', message: '', severity: 'info' });
    } catch (err) {
      if (err instanceof ApiError) setError(err.body?.detail || t('console.errorGeneric'));
      else setError(t('console.errorConnection'));
    }
    setSending(false);
  };

  return (
    <div className="space-y-5 max-w-lg">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.announcementsSection')}</h2>

      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-4">
        <FormRow label={t('console.announcementTitle')}>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder={t('console.announcementPlaceholder')}
            maxLength={200}
            className="w-full px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
          />
        </FormRow>

        <FormRow label={t('console.announcementSeverity')}>
          <div className="flex gap-2">
            {['info', 'warning'].map(sev => (
              <button
                key={sev}
                onClick={() => setForm(f => ({ ...f, severity: sev }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  form.severity === sev
                    ? sev === 'warning'
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/30'
                    : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
                }`}
              >
                {sev === 'info' ? `ℹ️ ${t('console.announcementInfo')}` : `⚠️ ${t('console.announcementWarning')}`}
              </button>
            ))}
          </div>
        </FormRow>

        <FormRow label={t('console.announcementMessage')}>
          <textarea
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            placeholder={t('console.announcementMsgPlaceholder')}
            rows={5}
            maxLength={2000}
            className="w-full px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none resize-none"
          />
          <p className="text-[10px] text-[var(--aif-text-muted)] text-right">{form.message.length}/2000</p>
        </FormRow>

        {/* Preview */}
        {form.title && form.message && (
          <div className={`rounded-lg p-3 border ${form.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[var(--aif-color-primary)]/5 border-[var(--aif-color-primary)]/20'}`}>
            <p className="text-[10px] text-[var(--aif-text-muted)] mb-1">{t('console.announcementPreview')}</p>
            <p className="text-xs font-semibold text-[var(--aif-text-primary)]">{form.title}</p>
            <p className="text-xs text-[var(--aif-text-secondary)] mt-0.5 line-clamp-3">{form.message}</p>
          </div>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}
        {result && (
          <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3">
            <p className="text-xs text-green-400">
              ✓ {t('console.announcementSent', { count: result.recipients })}
            </p>
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={sending || !form.title || !form.message}
          className="w-full py-3 rounded-xl text-sm font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-40 transition"
        >
          {sending ? '…' : t('console.announcementSend')}
        </button>
      </div>
    </div>
  );
}


// ── Settings ──────────────────────────────────────────────────

function ConsoleSettings({ token, t, user }) {
  const [form, setForm] = useState({
    app_name: '', logo_url: '', primary_color: '#2D5F2B',
    powered_by_visible: true, anthropic_api_key: '', llm_model_override: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/console/dashboard', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setForm(prev => ({ ...prev, app_name: d.app_name || 'AI Farm' }));
      })
      .catch(() => {});
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      if (form.app_name) payload.app_name = form.app_name;
      if (form.logo_url !== undefined) payload.logo_url = form.logo_url || null;
      if (form.primary_color) payload.primary_color = form.primary_color;
      payload.powered_by_visible = form.powered_by_visible;
      if (form.anthropic_api_key) payload.anthropic_api_key = form.anthropic_api_key;
      if (form.llm_model_override) payload.llm_model_override = form.llm_model_override;

      await apiClient.patch('/api/v1/aif/console/settings', payload, {
        entityType: 'console_settings', queueOnOffline: true,
      });
      setSaved(true);
      setForm(prev => ({ ...prev, anthropic_api_key: '' }));
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (err instanceof ApiError) setError(err.body?.detail || t('console.errorSave'));
      else setError(t('console.errorConnection'));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-lg">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.settings')}</h2>

      <section className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('console.branding')}</h3>
        <FormRow label={t('console.appName')}>
          <input type="text" value={form.app_name} onChange={e => setForm(f => ({ ...f, app_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
        </FormRow>
        <FormRow label={t('console.logoUrl')}>
          <input type="url" value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
            placeholder="https://…" className="w-full px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
        </FormRow>
        <FormRow label={t('console.primaryColor')}>
          <div className="flex gap-2 items-center">
            <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
              className="w-10 h-8 rounded cursor-pointer border border-[var(--aif-border-default)]" />
            <span className="text-xs font-mono text-[var(--aif-text-muted)]">{form.primary_color}</span>
          </div>
        </FormRow>
        <FormRow label={t('console.poweredBy')}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.powered_by_visible}
              onChange={e => setForm(f => ({ ...f, powered_by_visible: e.target.checked }))}
              className="w-4 h-4 accent-[var(--aif-color-primary)]" />
            <span className="text-xs text-[var(--aif-text-muted)]">{t('console.poweredByHint')}</span>
          </label>
        </FormRow>
      </section>

      <section className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('console.apiKey')}</h3>
        <p className="text-[11px] text-[var(--aif-text-muted)]">{t('console.apiKeyHint')}</p>
        <FormRow label="Anthropic API Key">
          <input type="password" value={form.anthropic_api_key}
            onChange={e => setForm(f => ({ ...f, anthropic_api_key: e.target.value }))}
            placeholder="sk-ant-api03-…" autoComplete="off"
            className="w-full px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none font-mono" />
        </FormRow>
        <p className="text-[10px] text-amber-400">{t('console.apiKeyWarning')}</p>
      </section>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-3 rounded-xl text-sm font-medium transition ${
          saved
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-[var(--aif-color-primary)] text-white hover:opacity-90'
        }`}
      >
        {saving ? '…' : saved ? `✓ ${t('console.saved')}` : t('console.saveSettings')}
      </button>
    </div>
  );
}


// ── Support ticket queue ──────────────────────────────────────

const PRIORITY_COLORS = {
  HIGH: 'text-red-400 bg-red-500/20',
  MEDIUM: 'text-amber-400 bg-amber-500/20',
  LOW: 'text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]',
};

const STATUS_COLORS = {
  OPEN: 'text-blue-400 bg-blue-500/20',
  IN_PROGRESS: 'text-amber-400 bg-amber-500/20',
  RESOLVED: 'text-green-400 bg-green-500/20',
  CLOSED: 'text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]',
};

function ConsoleSupport({ token, t }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [response, setResponse] = useState('');
  const [responseStatus, setResponseStatus] = useState('IN_PROGRESS');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const load = () => {
    setLoading(true);
    const params = filter ? `?status=${filter}` : '';
    Promise.all([
      apiClient.get(`/api/v1/aif/console/support/tickets${params}`).then(r => r.data).catch(() => null),
      apiClient.get('/api/v1/aif/console/support/stats').then(r => r.data).catch(() => null),
    ]).then(([t, s]) => {
      if (t) setTickets(t.tickets || []);
      if (s) setStats(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(); }, [token, filter]);

  const handleRespond = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/console/support/tickets/${selected.id}`, { status: responseStatus, admin_response: response }, {
        entityType: 'support_ticket', queueOnOffline: true,
      });
      setSelected(null);
      setResponse('');
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.supportTab')}</h2>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard value={stats.open_count} label={t('console.supportOpenCount')} icon="🎫" />
          <KpiCard value={stats.in_progress_count} label={t('console.supportInProgress')} icon="⚙️" />
          <KpiCard value={stats.this_week} label={t('console.supportThisWeek')} icon="📅" />
          <KpiCard
            value={stats.avg_resolution_hours != null ? `${stats.avg_resolution_hours}h` : '—'}
            label={t('console.supportAvgResolution')}
            icon="⏱️"
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-2.5 py-1 rounded-lg text-xs transition border ${
              filter === s
                ? 'bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)] border-[var(--aif-color-primary)]/30'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border-[var(--aif-border-default)]'
            }`}
          >
            {s || t('share.all')}
          </button>
        ))}
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="flex gap-4">
          {/* Ticket list */}
          <div className="flex-1 space-y-2">
            {tickets.length === 0 ? (
              <p className="text-sm text-center text-[var(--aif-text-muted)] py-8">{t('console.ticketNoTickets')}</p>
            ) : tickets.map(ticket => (
              <div
                key={ticket.id}
                onClick={() => { setSelected(ticket); setResponse(ticket.admin_response || ''); setResponseStatus(ticket.status === 'OPEN' ? 'IN_PROGRESS' : ticket.status); }}
                className={`rounded-xl border p-3 cursor-pointer transition ${
                  selected?.id === ticket.id
                    ? 'border-[var(--aif-color-primary)]/40 bg-[var(--aif-color-primary)]/5'
                    : 'border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-bg-elevated)]'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--aif-text-primary)] truncate">{ticket.subject}</p>
                    <p className="text-[10px] text-[var(--aif-text-muted)] mt-0.5">{ticket.user_email || t('console.user')}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[ticket.priority] || ''}`}>
                      {ticket.priority}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[ticket.status] || ''}`}>
                      {ticket.status}
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-[var(--aif-text-muted)] mt-1 line-clamp-2">{ticket.message}</p>
              </div>
            ))}
          </div>

          {/* Response panel */}
          {selected && (
            <div className="w-72 flex-shrink-0 space-y-3">
              <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
                <h4 className="text-xs font-semibold text-[var(--aif-text-primary)] mb-2">{selected.subject}</h4>
                <p className="text-xs text-[var(--aif-text-secondary)] mb-3">{selected.message}</p>

                <select
                  value={responseStatus}
                  onChange={e => setResponseStatus(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none mb-2"
                >
                  {['IN_PROGRESS', 'RESOLVED', 'CLOSED'].map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>

                <textarea
                  value={response}
                  onChange={e => setResponse(e.target.value)}
                  placeholder={t('console.ticketAdminResponse')}
                  rows={4}
                  className="w-full px-2 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none resize-none"
                />

                <button
                  onClick={handleRespond}
                  disabled={saving}
                  className="w-full mt-2 py-1.5 rounded-lg text-xs font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-40 transition"
                >
                  {saving ? '…' : t('console.ticketSendResponse')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Content (FAQ articles) ────────────────────────────────────

const FAQ_CATEGORIES = ['GETTING_STARTED', 'FEATURES', 'BILLING', 'TROUBLESHOOTING'];

function ConsoleContent({ token, t }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'FEATURES', published: false, locale: 'de', sort_order: 0 });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetch('/api/v1/aif/console/faq', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setArticles(d.articles || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    const isEdit = !!editing;
    const url = isEdit ? `/api/v1/aif/console/faq/${editing.id}` : '/api/v1/aif/console/faq';
    const method = isEdit ? 'PATCH' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setShowCreate(false);
      setEditing(null);
      setForm({ title: '', content: '', category: 'FEATURES', published: false, locale: 'de', sort_order: 0 });
      load();
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/console/faq/${id}`, {
        entityType: 'faq', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await apiClient.patch(`/api/v1/aif/console/faq/${article.id}`, { published: !article.published }, {
        entityType: 'faq', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, content: a.content, category: a.category, published: a.published, locale: a.locale, sort_order: a.sort_order });
    setShowCreate(true);
  };

  const catLabel = (cat) => ({
    GETTING_STARTED: t('console.faqCatGettingStarted'),
    FEATURES: t('console.faqCatFeatures'),
    BILLING: t('console.faqCatBilling'),
    TROUBLESHOOTING: t('console.faqCatTroubleshooting'),
  }[cat] || cat);

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.faqTitle')}</h2>
        <button
          onClick={() => { setShowCreate(s => !s); setEditing(null); setForm({ title: '', content: '', category: 'FEATURES', published: false, locale: 'de', sort_order: 0 }); }}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90"
        >
          {showCreate && !editing ? `✕ ${t('form.cancel')}` : `+ ${t('console.faqCreate')}`}
        </button>
      </div>

      {/* Create / Edit form */}
      {showCreate && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">
            {editing ? t('console.faqEdit') : t('console.faqCreate')}
          </h3>
          <FormRow label={t('console.faqArticleTitle')}>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
          </FormRow>

          <div className="grid grid-cols-2 gap-3">
            <FormRow label={t('console.faqArticleCategory')}>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                {FAQ_CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
            </FormRow>
            <FormRow label={t('console.faqLanguage')}>
              <select value={form.locale} onChange={e => setForm(f => ({ ...f, locale: e.target.value }))}
                className="w-full px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </FormRow>
          </div>

          <FormRow label={t('console.faqArticleContent')}>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              rows={8} placeholder="# Überschrift\n\nText mit **Markdown**…"
              className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none resize-none font-mono" />
          </FormRow>

          <label className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)] cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
              className="accent-[var(--aif-color-primary)]" />
            {t('console.faqPublishLabel')}
          </label>

          <button onClick={handleSave} disabled={saving || !form.title || !form.content}
            className="w-full py-2 rounded-lg text-sm font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-40 transition">
            {saving ? '…' : t('console.faqSaved')}
          </button>
        </div>
      )}

      {/* Article list */}
      {loading ? <LoadingSpinner /> : articles.length === 0 ? (
        <p className="text-center text-sm text-[var(--aif-text-muted)] py-8">{t('console.faqNoArticles')}</p>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <div key={a.id} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--aif-text-primary)] truncate">{a.title}</span>
                    {a.published
                      ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">✓ {t('console.faqPublished')}</span>
                      : <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">{t('console.faqDraft')}</span>
                    }
                  </div>
                  <div className="flex gap-2 mt-0.5">
                    <span className="text-[10px] text-[var(--aif-text-muted)]">{catLabel(a.category)}</span>
                    <span className="text-[10px] text-[var(--aif-text-muted)]">{a.locale}</span>
                  </div>
                </div>
                <div className="flex gap-1.5 ml-2">
                  <button onClick={() => handleTogglePublish(a)}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]">
                    {a.published ? t('console.faqUnpublish') : t('console.faqPublish')}
                  </button>
                  <button onClick={() => startEdit(a)}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]">
                    {t('console.faqEditBtn')}
                  </button>
                  <button onClick={() => handleDelete(a.id)}
                    className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">
                    {t('form.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Subsidy data management ───────────────────────────────────

function ConsoleSubsidyData({ token, t }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount_per_ha: '', deadline: '', portal_url: '', verified: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = (q = search) => {
    setLoading(true);
    const params = new URLSearchParams({ country_code: 'DE' });
    if (q) params.set('search', q);
    apiClient.get(`/api/v1/aif/console/subsidies?${params}`)
      .then(r => { if (r.data) setPrograms(r.data.programs || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(); }, [token]);

  const handleEdit = (p) => {
    setEditing(p);
    setForm({
      amount_per_ha: p.amount_per_ha || '',
      deadline: p.deadline || '',
      portal_url: p.portal_url || '',
      verified: p.verified || false,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = { verified: form.verified };
    if (form.amount_per_ha) payload.amount_per_ha = parseFloat(form.amount_per_ha);
    if (form.deadline) payload.deadline = form.deadline;
    if (form.portal_url) payload.portal_url = form.portal_url;
    try {
      await apiClient.patch(`/api/v1/aif/console/subsidies/${editing.id}`, payload, {
        entityType: 'subsidy', queueOnOffline: true,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditing(null);
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const handleReset = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/console/subsidies/${id}/override`, {
        entityType: 'subsidy', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.subsidyTab')}</h2>
      <p className="text-xs text-[var(--aif-text-muted)]">Passe Förderbeträge und Fristen an — ohne Code-Deployment. Änderungen wirken sofort.</p>

      {/* Search */}
      <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t('console.subsidySearch')}
          className="flex-1 px-3 py-2 rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
        <button type="submit" className="px-4 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm">{t('console.search')}</button>
      </form>

      {loading ? <LoadingSpinner /> : (
        <div className="rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[var(--aif-bg-surface)]">
              <tr>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">Programm</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.subsidyOverrideAmount')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">{t('console.subsidyOverrideDeadline')}</th>
                <th className="text-center px-3 py-2 text-[var(--aif-text-muted)]">Override</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {programs.slice(0, 30).map((p, i) => (
                <tr key={p.id} className={`border-t border-[var(--aif-border-default)] ${i % 2 === 0 ? 'bg-[var(--aif-bg-page)]' : 'bg-[var(--aif-bg-surface)]'}`}>
                  <td className="px-3 py-2">
                    <div className="text-[var(--aif-text-primary)] truncate max-w-48">{p.name_de || p.name}</div>
                    <div className="text-[10px] text-[var(--aif-text-muted)] font-mono">{p.id}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={p.has_override ? 'text-amber-400 font-medium' : 'text-[var(--aif-text-secondary)]'}>
                      {p.amount_per_ha > 0 ? `${p.amount_per_ha} €/ha` : '—'}
                    </span>
                    {p.has_override && <div className="text-[9px] text-[var(--aif-text-muted)]">orig: {p.original_amount}</div>}
                  </td>
                  <td className="px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell text-[10px]">
                    {p.deadline ? new Date(p.deadline).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {p.has_override
                      ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">✓ override</span>
                      : p.verified
                        ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">✓</span>
                        : null}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(p)}
                        className="text-[10px] px-2 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]">
                        Bearbeiten
                      </button>
                      {p.has_override && (
                        <button onClick={() => handleReset(p.id)}
                          className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400">
                          Reset
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit panel */}
      {editing && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-amber-400">{editing.name_de || editing.name}</h3>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label={`${t('console.subsidyOverrideAmount')} (EUR/ha)`}>
              <input type="number" step="0.01" value={form.amount_per_ha} onChange={e => setForm(f => ({ ...f, amount_per_ha: e.target.value }))}
                placeholder={`Standard: ${editing.original_amount}`}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
            </FormRow>
            <FormRow label={t('console.subsidyOverrideDeadline')}>
              <input type="date" value={form.deadline || ''} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
            </FormRow>
          </div>
          <FormRow label={t('console.subsidyOverridePortal')}>
            <input type="url" value={form.portal_url || ''} onChange={e => setForm(f => ({ ...f, portal_url: e.target.value }))}
              placeholder="https://…"
              className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
          </FormRow>
          <label className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)] cursor-pointer">
            <input type="checkbox" checked={form.verified} onChange={e => setForm(f => ({ ...f, verified: e.target.checked }))}
              className="accent-amber-400" />
            {t('console.subsidyVerified')} (Betrag manuell geprüft)
          </label>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:opacity-90 disabled:opacity-40 transition">
              {saving ? '…' : saved ? '✓ Gespeichert' : t('console.subsidySaveOverride')}
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--aif-text-muted)]">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Reference data management ─────────────────────────────────

function ConsoleReferenceData({ token, t }) {
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('DE');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ yield_t_ha: '', market_price_t: '', subsidy_per_ha: '' });
  const [saving, setSaving] = useState(false);

  const load = (cc = country) => {
    setLoading(true);
    apiClient.get(`/api/v1/aif/console/reference-data?country_code=${cc}`)
      .then(r => { if (r.data) setCrops(r.data.crops || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(country); }, [token, country]);

  const handleEdit = (crop) => {
    setEditing(crop);
    setForm({ yield_t_ha: crop.yield_t_ha || '', market_price_t: crop.market_price_t || '', subsidy_per_ha: crop.subsidy_per_ha || '' });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = {};
    if (form.yield_t_ha) payload.yield_t_ha = parseFloat(form.yield_t_ha);
    if (form.market_price_t) payload.market_price_t = parseFloat(form.market_price_t);
    if (form.subsidy_per_ha) payload.subsidy_per_ha = parseFloat(form.subsidy_per_ha);
    try {
      await apiClient.patch(`/api/v1/aif/console/reference-data/${editing.country_code}/${editing.crop_code}`, payload, {
        entityType: 'reference_data', queueOnOffline: true,
      });
      setEditing(null); load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const handleReset = async (crop) => {
    try {
      await apiClient.delete(`/api/v1/aif/console/reference-data/${crop.country_code}/${crop.crop_code}`, {
        entityType: 'reference_data', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  const COUNTRIES = ['DE', 'AT', 'CH', 'PL', 'FR', 'GB', 'NL', 'IT', 'ES', 'IE'];

  return (
    <div className="space-y-4 max-w-4xl">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.referenceTitle')}</h2>
      <p className="text-xs text-[var(--aif-text-muted)]">Überschreibe KTBL-Referenzwerte für deine Region. Betrifft den Deckungsbeitrags-Rechner aller Kunden.</p>

      {/* Country selector */}
      <div className="flex gap-2 flex-wrap">
        {COUNTRIES.map(cc => (
          <button key={cc} onClick={() => setCountry(cc)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition border ${
              country === cc
                ? 'bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)] border-[var(--aif-color-primary)]/30'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border-[var(--aif-border-default)]'
            }`}>
            {cc}
          </button>
        ))}
      </div>

      {loading ? <LoadingSpinner /> : crops.length === 0 ? (
        <p className="text-sm text-center text-[var(--aif-text-muted)] py-8">{t('console.referenceNoData')}</p>
      ) : (
        <div className="rounded-xl border border-[var(--aif-border-default)] overflow-x-auto">
          <table className="w-full text-xs min-w-[520px]">
            <thead className="bg-[var(--aif-bg-surface)]">
              <tr>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.referenceCrop')}</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.referenceYield')}</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.referencePrice')}</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.referenceSubsidy')}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {crops.map((c, i) => (
                <tr key={c.crop_code} className={`border-t border-[var(--aif-border-default)] ${i % 2 === 0 ? 'bg-[var(--aif-bg-page)]' : 'bg-[var(--aif-bg-surface)]'}`}>
                  <td className="px-3 py-2">
                    <span className="font-medium text-[var(--aif-text-primary)]">{c.crop_code}</span>
                    {c.has_override && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400">override</span>}
                  </td>
                  <td className={`px-3 py-2 text-right ${c.has_override ? 'text-amber-400' : 'text-[var(--aif-text-secondary)]'}`}>
                    {c.yield_t_ha}
                  </td>
                  <td className={`px-3 py-2 text-right ${c.has_override ? 'text-amber-400' : 'text-[var(--aif-text-secondary)]'}`}>
                    {c.market_price_t}
                  </td>
                  <td className={`px-3 py-2 text-right ${c.has_override ? 'text-amber-400' : 'text-[var(--aif-text-secondary)]'}`}>
                    {c.subsidy_per_ha}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(c)}
                        className="text-[10px] px-2 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]">{t('ui.edit')}</button>
                      {c.has_override && (
                        <button onClick={() => handleReset(c)}
                          className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400">{t('ui.reset')}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-amber-400">{editing.crop_code} / {editing.country_code}</h3>
          <div className="grid grid-cols-3 gap-3">
            <FormRow label={t('console.referenceYield')}>
              <input type="number" step="0.1" value={form.yield_t_ha} onChange={e => setForm(f => ({ ...f, yield_t_ha: e.target.value }))}
                placeholder={`Standard: ${editing.original_yield}`}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
            </FormRow>
            <FormRow label={t('console.referencePrice')}>
              <input type="number" step="1" value={form.market_price_t} onChange={e => setForm(f => ({ ...f, market_price_t: e.target.value }))}
                placeholder={`Standard: ${editing.original_price}`}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
            </FormRow>
            <FormRow label={t('console.referenceSubsidy')}>
              <input type="number" step="1" value={form.subsidy_per_ha} onChange={e => setForm(f => ({ ...f, subsidy_per_ha: e.target.value }))}
                placeholder={`Standard: ${editing.original_subsidy}`}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none" />
            </FormRow>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:opacity-90 disabled:opacity-40">
              {saving ? '…' : t('console.referenceSaveOverride')}
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--aif-text-muted)]">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Revenue ───────────────────────────────────────────────────

function ConsoleRevenue({ token, t }) {
  const [data, setData] = useState(null);
  const [perCustomer, setPerCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/api/v1/aif/console/revenue', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch('/api/v1/aif/console/revenue/per-customer', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([rev, pc]) => { setData(rev); setPerCustomer(pc); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <ErrorCard message={t('console.loadError')} />;

  const split = data.revenue_split || {};
  const trend = data.revenue_trend || [];

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.revenueDashboard')}</h2>
        <p className="text-[10px] text-amber-400 mt-0.5">{t('console.revenueNote')}</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard value={`${data.mrr?.toLocaleString('de-DE')} €`} label="MRR" icon="💶" />
        <KpiCard value={`${data.arr?.toLocaleString('de-DE')} €`} label={t('console.kpiArr')} icon="📈" />
        <KpiCard value={`${data.arpu} €`} label={t('console.kpiArpu')} icon="👤" />
        <KpiCard value={data.churn_rate_pct != null ? `${data.churn_rate_pct}%` : '—'} label={t('console.kpiChurnRate')} icon="🔄" />
        <KpiCard
          value={data.ltv != null ? `${data.ltv?.toLocaleString('de-DE')} €` : '∞'}
          label={t('console.kpiLtv')}
          icon="♾️"
        />
        <KpiCard
          value={`${data.total_revenue_lifetime_estimate?.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`}
          label={t('console.kpiLifetimeRevenue')}
          icon="💰"
        />
      </div>

      {/* Revenue split */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
          {t('console.revenueSplitTitle')}
        </h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 text-center p-3 rounded-lg bg-[var(--aif-bg-elevated)]">
            <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('console.revenueSplitGross')}</div>
            <div className="text-xl font-bold text-[var(--aif-text-primary)]">{split.gross} €</div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">/Monat</div>
          </div>
          <div className="flex items-center text-[var(--aif-text-muted)] text-xl">→</div>
          <div className="flex-1 text-center p-3 rounded-lg bg-[var(--aif-color-primary)]/5">
            <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('console.revenueSplitAirfarm')} ({split.split_pct}%)</div>
            <div className="text-xl font-bold text-[var(--aif-color-primary)]">{split.tenant_share} €</div>
          </div>
          <div className="flex-1 text-center p-3 rounded-lg bg-purple-500/5">
            <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('console.revenueSplitAlcedo')} ({100 - split.split_pct}%)</div>
            <div className="text-xl font-bold text-purple-400">{split.alcedo_share} €</div>
          </div>
        </div>
      </div>

      {/* MRR Trend */}
      {trend.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-3">
            {t('console.revenueTrend')}
          </h3>
          <div className="flex items-end gap-2 h-16">
            {trend.map((m, i) => {
              const maxMrr = Math.max(...trend.map(x => x.mrr_estimate || 0), 1);
              const h = Math.round(((m.mrr_estimate || 0) / maxMrr) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t bg-[var(--aif-color-primary)]/60"
                    style={{ height: `${h}%`, minHeight: m.mrr_estimate > 0 ? '4px' : '0' }}
                    title={`${m.month}: ${m.mrr_estimate} €`}
                  />
                  <span className="text-[8px] text-[var(--aif-text-muted)] rotate-45 origin-left">{m.month?.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-[var(--aif-text-muted)] mt-2 text-center">
            * Historische MRR-Daten werden ab Sprint 48 (Stripe Live) verfügbar
          </p>
        </div>
      )}

      {/* Per-customer revenue table */}
      {perCustomer && perCustomer.customers.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
          <div className="bg-[var(--aif-bg-surface)] px-3 py-2 text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">
            {t('console.revenuePerCustomer')}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[var(--aif-bg-surface)]">
              <tr>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.colName')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.colTier')}</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.colMonthsActive')}</th>
                <th className="text-right px-3 py-2 text-[var(--aif-text-muted)]">{t('console.colTotalPaid')}</th>
              </tr>
            </thead>
            <tbody>
              {perCustomer.customers.map((c, i) => (
                <tr key={c.user_id} className={`border-t border-[var(--aif-border-default)] ${i % 2 === 0 ? 'bg-[var(--aif-bg-page)]' : 'bg-[var(--aif-bg-surface)]'}`}>
                  <td className="px-3 py-2">
                    <div className="text-[var(--aif-text-primary)]">{c.name}</div>
                    <div className="text-[var(--aif-text-muted)] text-[10px]">{c.email}</div>
                  </td>
                  <td className="px-3 py-2"><TierBadge tier={c.tier} /></td>
                  <td className="px-3 py-2 text-right text-[var(--aif-text-secondary)]">{c.months_active}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[var(--aif-color-primary)]">
                    {c.total_paid_estimate?.toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Subscriptions (enhanced Sprint 42) ────────────────────────

function ConsoleSubscriptions({ token, t, onSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/console/subscriptions', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <ErrorCard message={t('console.loadError')} />;

  const { active_subscriptions = [], trial_users = [], at_risk = [], recently_churned = [], counts = {} } = data;

  return (
    <div className="space-y-5 max-w-3xl">
      <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.subsTab')}</h2>

      {/* Summary counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard value={counts.active || 0} label={t('console.subsActive')} icon="✅" />
        <KpiCard value={counts.trial || 0} label={t('console.subsTrial')} icon="🌱" />
        <KpiCard value={counts.at_risk || 0} label={t('console.subsAtRisk')} icon="⚠️" />
        <KpiCard value={counts.churned_30d || 0} label={t('console.subsChurned')} icon="👋" />
      </div>

      {/* Active subscriptions */}
      <SubSection title={t('console.subsActive')} count={active_subscriptions.length} color="green">
        {active_subscriptions.length === 0 ? (
          <p className="text-xs text-[var(--aif-text-muted)] py-2">{t('console.noCustomers')}</p>
        ) : (
          <div className="space-y-1">
            {active_subscriptions.map(u => (
              <SubCustomerRow key={u.user_id} u={u} onSelect={onSelect} t={t} />
            ))}
          </div>
        )}
      </SubSection>

      {/* Trial users */}
      <SubSection title={t('console.subsTrial')} count={trial_users.length} color="blue">
        {trial_users.length === 0 ? (
          <p className="text-xs text-[var(--aif-text-muted)] py-2">{t('console.noTrialUsers')}</p>
        ) : (
          <div className="space-y-1">
            {trial_users.map(u => (
              <SubCustomerRow
                key={u.user_id} u={u} onSelect={onSelect} t={t}
                badge={<span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{t('console.trialHint')}</span>}
              />
            ))}
          </div>
        )}
      </SubSection>

      {/* At risk */}
      {at_risk.length > 0 && (
        <SubSection title={t('console.subsAtRisk')} count={at_risk.length} color="amber">
          {at_risk.map(u => (
            <SubCustomerRow
              key={u.user_id} u={u} onSelect={onSelect} t={t}
              badge={<span className="text-[9px] text-amber-400">{t('console.atRiskHint', { days: u.days_inactive || '14+' })}</span>}
            />
          ))}
        </SubSection>
      )}

      {/* Recently churned */}
      {recently_churned.length > 0 && (
        <SubSection title={t('console.subsChurned')} count={recently_churned.length} color="red">
          {recently_churned.map((u, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg text-xs">
              <div>
                <span className="text-[var(--aif-text-primary)]">{u.name}</span>
                <span className="text-[var(--aif-text-muted)] ml-2 text-[10px]">{u.email}</span>
              </div>
              <div className="text-right text-[10px] text-[var(--aif-text-muted)]">
                {u.tier_before} → free
                {u.churned_at && <span className="ml-1">{new Date(u.churned_at).toLocaleDateString('de-DE')}</span>}
              </div>
            </div>
          ))}
        </SubSection>
      )}
    </div>
  );
}

function SubSection({ title, count, color, children }) {
  const colors = {
    green: 'border-green-500/30 bg-green-500/5',
    blue: 'border-blue-500/30 bg-blue-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    red: 'border-red-500/30 bg-red-500/5',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.green}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)]">{title}</h3>
        <span className="text-[10px] text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)] px-1.5 py-0.5 rounded-full">{count}</span>
      </div>
      {children}
    </div>
  );
}

function SubCustomerRow({ u, onSelect, t, badge }) {
  return (
    <div
      onClick={() => onSelect(u.user_id)}
      className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-[var(--aif-bg-elevated)] cursor-pointer transition text-xs"
    >
      <div className="flex items-center gap-2">
        <TierBadge tier={u.tier} />
        <div>
          <span className="text-[var(--aif-text-primary)]">{u.name}</span>
          <span className="text-[var(--aif-text-muted)] ml-2 text-[10px]">{u.email}</span>
        </div>
        {badge && <span className="ml-1">{badge}</span>}
      </div>
      <span className="text-[10px] text-[var(--aif-text-muted)]">
        {u.months_active != null ? t('console.subsMonthsActive', { months: u.months_active }) : ''}
      </span>
    </div>
  );
}


// ── Coupons ───────────────────────────────────────────────────

const COUPON_TYPES = ['PERCENTAGE', 'FIXED_AMOUNT', 'FREE_MONTHS'];

function ConsoleCoupons({ token, t }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'PERCENTAGE', discount_value: 50,
    valid_for_tiers: ['premium', 'pro'], max_uses: '', valid_from: new Date().toISOString().split('T')[0], valid_to: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = () => {
    setLoading(true);
    fetch('/api/v1/aif/console/coupons', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCoupons(d.coupons || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(); }, [token]);

  const handleCreate = async () => {
    setSaving(true); setError(null);
    const payload = {
      code: form.code,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value),
      valid_for_tiers: form.valid_for_tiers,
      valid_from: form.valid_from,
    };
    if (form.max_uses) payload.max_uses = parseInt(form.max_uses);
    if (form.valid_to) payload.valid_to = form.valid_to;
    try {
      await apiClient.post('/api/v1/aif/console/coupons', payload, {
        entityType: 'coupon', queueOnOffline: true,
      });
      setShowCreate(false);
      setForm({ code: '', discount_type: 'PERCENTAGE', discount_value: 50, valid_for_tiers: ['premium', 'pro'], max_uses: '', valid_from: new Date().toISOString().split('T')[0], valid_to: '' });
      load();
    } catch (err) {
      if (err instanceof ApiError) setError(err.body?.detail || t('console.errorGeneric'));
      else setError(t('console.errorConnection'));
    }
    setSaving(false);
  };

  const handleDisable = async (id) => {
    try {
      await apiClient.patch(`/api/v1/aif/console/coupons/${id}`, { status: 'DISABLED' }, {
        entityType: 'coupon', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleTier = (tier) => {
    setForm(f => ({
      ...f,
      valid_for_tiers: f.valid_for_tiers.includes(tier)
        ? f.valid_for_tiers.filter(t => t !== tier)
        : [...f.valid_for_tiers, tier],
    }));
  };

  const typeLabel = (type) => ({
    PERCENTAGE: '% Rabatt',
    FIXED_AMOUNT: 'EUR Rabatt',
    FREE_MONTHS: 'Gratis-Monate',
  }[type] || type);

  const statusColor = (status) => ({
    ACTIVE: 'text-green-400 bg-green-500/20',
    EXPIRED: 'text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]',
    DISABLED: 'text-red-400 bg-red-500/20',
    EXHAUSTED: 'text-amber-400 bg-amber-500/20',
  }[status] || 'text-[var(--aif-text-muted)]');

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('console.couponsTitle')}</h2>
        <button
          onClick={() => setShowCreate(s => !s)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition"
        >
          {showCreate ? '✕ Abbrechen' : `+ ${t('console.couponCreate')}`}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('console.couponCreate')}</h3>

          <div className="grid grid-cols-2 gap-3">
            <FormRow label={t('console.couponCode')}>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="BETA2026"
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] font-mono outline-none"
              />
            </FormRow>

            <FormRow label={t('console.couponType')}>
              <select
                value={form.discount_type}
                onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
              >
                {COUPON_TYPES.map(type => (
                  <option key={type} value={type}>{typeLabel(type)}</option>
                ))}
              </select>
            </FormRow>

            <FormRow label={`${t('console.couponValue')} (${form.discount_type === 'PERCENTAGE' ? '%' : form.discount_type === 'FREE_MONTHS' ? t('console.months') : 'EUR'})`}>
              <input
                type="number"
                min="1"
                max={form.discount_type === 'PERCENTAGE' ? 100 : undefined}
                value={form.discount_value}
                onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
              />
            </FormRow>

            <FormRow label={t('console.couponMaxUses')}>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                placeholder="∞"
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
              />
            </FormRow>

            <FormRow label={t('console.couponValidFrom')}>
              <input
                type="date"
                value={form.valid_from}
                onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
              />
            </FormRow>

            <FormRow label={t('console.couponValidTo')}>
              <input
                type="date"
                value={form.valid_to}
                onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] outline-none"
              />
            </FormRow>
          </div>

          <FormRow label={t('console.couponTiers')}>
            <div className="flex gap-2">
              {['free', 'premium', 'pro'].map(tier => (
                <button
                  key={tier}
                  onClick={() => toggleTier(tier)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition border ${
                    form.valid_for_tiers.includes(tier)
                      ? 'bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)] border-[var(--aif-color-primary)]/30'
                      : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] border-[var(--aif-border-default)]'
                  }`}
                >
                  {tier}
                </button>
              ))}
            </div>
          </FormRow>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <button
            onClick={handleCreate}
            disabled={saving || !form.code}
            className="w-full py-2.5 rounded-lg text-sm font-medium bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-40 transition"
          >
            {saving ? '…' : t('console.couponSave')}
          </button>
        </div>
      )}

      {/* Coupon list */}
      {loading ? <LoadingSpinner /> : coupons.length === 0 ? (
        <div className="text-center py-8 text-sm text-[var(--aif-text-muted)]">{t('console.noCoupons')}</div>
      ) : (
        <div className="rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[var(--aif-bg-surface)]">
              <tr>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.couponCode')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">{t('console.couponType')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.couponValue')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.couponUses')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">{t('console.couponValidTo')}</th>
                <th className="text-left px-3 py-2 text-[var(--aif-text-muted)]">{t('console.couponStatus')}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c, i) => (
                <tr key={c.id} className={`border-t border-[var(--aif-border-default)] ${i % 2 === 0 ? 'bg-[var(--aif-bg-page)]' : 'bg-[var(--aif-bg-surface)]'}`}>
                  <td className="px-3 py-2">
                    <span className="font-mono font-semibold text-[var(--aif-text-primary)]">{c.code}</span>
                    {c.valid_for_tiers?.length > 0 && (
                      <div className="flex gap-1 mt-0.5">
                        {c.valid_for_tiers.map(tier => <TierBadge key={tier} tier={tier} />)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">{typeLabel(c.discount_type)}</td>
                  <td className="px-3 py-2 text-[var(--aif-text-secondary)]">
                    {c.discount_type === 'PERCENTAGE' ? `${c.discount_value}%` :
                     c.discount_type === 'FREE_MONTHS' ? `${c.discount_value} Mo.` :
                     `${c.discount_value} €`}
                  </td>
                  <td className="px-3 py-2 text-[var(--aif-text-secondary)]">
                    {c.current_uses}{c.max_uses ? `/${c.max_uses}` : ''}
                  </td>
                  <td className="px-3 py-2 text-[var(--aif-text-muted)] hidden sm:table-cell">
                    {c.valid_to ? new Date(c.valid_to).toLocaleDateString('de-DE') : '∞'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${statusColor(c.status)}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => copyCode(c.code)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]"
                      >
                        {copied === c.code ? t('console.couponCopied') : t('console.couponCopyCode')}
                      </button>
                      {c.status === 'ACTIVE' && (
                        <button
                          onClick={() => handleDisable(c.id)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        >
                          {t('console.couponDisable')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Shared UI helpers ─────────────────────────────────────────

function KpiCard({ value, label, icon }) {
  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-center">
      <div className="text-lg mb-0.5">{icon}</div>
      <div className="text-lg font-bold text-[var(--aif-text-primary)]">
        {value != null ? (typeof value === 'number' ? value.toLocaleString('de-DE') : value) : '—'}
      </div>
      <div className="text-[10px] text-[var(--aif-text-muted)] leading-tight mt-0.5">{label}</div>
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="rounded-lg border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-2.5">
      <div className="text-[10px] text-[var(--aif-text-muted)]">{label}</div>
      <div className="text-sm font-semibold text-[var(--aif-text-primary)] mt-0.5">
        {value != null ? value : '—'}
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const colors = {
    pro: 'bg-purple-500/20 text-purple-400',
    premium: 'bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)]',
    enterprise: 'bg-amber-500/20 text-amber-400',
    free: 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]',
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${colors[tier] || colors.free}`}>
      {tier || 'free'}
    </span>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-[var(--aif-text-muted)]">{label}</label>
      {children}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-[var(--aif-color-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-400">
      {message}
    </div>
  );
}

function MiniBarChart({ data, valueKey, labelKey }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div
            className="w-full bg-[var(--aif-color-primary)]/60 rounded-t transition-all"
            style={{ height: `${((d[valueKey] || 0) / maxVal) * 100}%`, minHeight: d[valueKey] > 0 ? '4px' : '0' }}
            title={`${d[labelKey]}: ${d[valueKey]}`}
          />
        </div>
      ))}
    </div>
  );
}
